import type { DiffLine } from '@fos/shared/contracts/context';
import { cn } from '../../../lib/cn';

type Row = { kind: 'line'; line: DiffLine; key: string } | { kind: 'gap'; count: number; key: string };

/** Wiersze do pokazania: zmienione + `context` linii wokół, dłuższe niezmienione odcinki zwinięte. */
export function diffRows(lines: readonly DiffLine[], context = 2): Row[] {
  const keep = lines.map(() => false);
  lines.forEach((l, i) => {
    if (l.op === 'same') return;
    for (let k = Math.max(0, i - context); k <= Math.min(lines.length - 1, i + context); k++) keep[k] = true;
  });
  const rows: Row[] = [];
  let gap = 0;
  lines.forEach((line, i) => {
    if (keep[i]) {
      if (gap > 0) rows.push({ kind: 'gap', count: gap, key: `g${i}` });
      gap = 0;
      rows.push({ kind: 'line', line, key: `l${i}` });
    } else {
      gap++;
    }
  });
  if (gap > 0 && rows.length > 0) rows.push({ kind: 'gap', count: gap, key: 'g-end' });
  return rows;
}

function gapLabel(n: number): string {
  if (n === 1) return '… 1 linia bez zmian';
  const last = n % 10;
  const lastTwo = n % 100;
  const form = last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? 'linie' : 'linii';
  return `… ${n} ${form} bez zmian`;
}

/**
 * Różnice linia po linii (historia profili, propozycje pamięci, wersje notatek): dodane na zielonym tle z „+”,
 * usunięte na czerwonym z „−”, niezmienione odcinki zwinięte. Długie linie się zawijają (bez przewijania w poziomie).
 */
export function DiffView({
  lines,
  context = 2,
  emptyText = 'Brak zmian.',
  className,
  'aria-label': ariaLabel = 'Zmiany',
}: {
  lines: readonly DiffLine[];
  context?: number;
  emptyText?: string;
  className?: string;
  'aria-label'?: string;
}) {
  const changed = lines.some((l) => l.op !== 'same');
  if (!changed) return <p className={cn('text-body-s text-fg-muted', className)}>{emptyText}</p>;
  const rows = diffRows(lines, context);
  return (
    <figure
      aria-label={ariaLabel}
      className={cn('overflow-hidden rounded-lg border border-line font-mono text-caption leading-[1.6]', className)}
    >
      {rows.map((row) =>
        row.kind === 'gap' ? (
          <div key={row.key} className="bg-surface px-3 py-0.5 font-sans text-fg-muted">
            {gapLabel(row.count)}
          </div>
        ) : (
          <div
            key={row.key}
            className={cn(
              'flex gap-2 px-3',
              row.line.op === 'add' && 'bg-success-subtle text-fg',
              row.line.op === 'del' && 'bg-danger-subtle text-fg-secondary',
              row.line.op === 'same' && 'text-fg-secondary',
            )}
          >
            <span aria-hidden className="w-3 shrink-0 select-none text-fg-muted">
              {row.line.op === 'add' ? '+' : row.line.op === 'del' ? '−' : ' '}
            </span>
            {row.line.op !== 'same' && (
              <span className="sr-only">{row.line.op === 'add' ? 'Dodano: ' : 'Usunięto: '}</span>
            )}
            <span className="min-w-0 flex-1 break-words whitespace-pre-wrap">{row.line.text || ' '}</span>
          </div>
        ),
      )}
    </figure>
  );
}
